/**
 * SOLOMON — ACTION CARD
 * Displays a proposed action with Approve / Edit / Cancel controls.
 * Rendered inline in the chat when Solomon proposes an external action.
 *
 * Glass card styling following design-system.md.
 */

import { useState } from 'react';
import { mcpCardView } from './mcpCardView.js';
import { getModelLabel as resolveModelLabel } from '../../lib/modelRegistry.js';
import { useSolomon } from '../../context/SolomonContext';
import './ActionCard.css';

/**
 * Sub-model granularity (Opus / GPT-mini / …), falling back to the raw ID for a
 * model the registry does not recognise — this surface would rather show
 * 'llama-3' than nothing. Until 2026-08-07 the local fallback here hardcoded
 * 'GPT-4o' for every /gpt/ match and so mislabelled every GPT-5.x action card.
 */
function getModelLabel(model) {
  if (!model) return null;
  return resolveModelLabel(model) ?? model;
}

export default function ActionCard({
  action,
  onApprove,
  onEdit,
  onCancel,
  onAlwaysAllow,
  onAllowForChat,
}) {
  const [loading, setLoading] = useState(null); // 'approve' | 'cancel' | null
  const [cardError, setCardError] = useState(null);
  const { openSettings } = useSolomon();

  if (!action) return null;

  const {
    id,
    action_type,
    action_description,
    model_used,
    confidence_score,
    reversible,
  } = action;

  // action_payload ({serverId, toolName, paid}) is read inside mcpCardView —
  // not destructured here since nothing else in this component needs it raw.
  const mcp = mcpCardView(action);

  const handleApprove = async () => {
    setLoading('approve'); setCardError(null);
    try {
      await onApprove?.(id);
    } catch (err) {
      // A failed execution must never be silent: the card is the only place
      // the operator is looking.
      setCardError('That did not go through — nothing was run. Ask me to try again.');
      console.warn('[Solomon] approve failed:', err);
    } finally {
      setLoading(null);
    }
  };

  const handleCancel = async () => {
    setLoading('cancel');
    try {
      await onCancel?.(id);
    } finally {
      setLoading(null);
    }
  };

  const typeLabel = action_type?.replace(/_/g, ' ') || 'Action';
  const modelLabel = getModelLabel(model_used);

  return (
    <div className="action-card">
      {/* Header */}
      <div className="action-card-header">
        <div className="action-card-type-badge">
          <ShieldIcon />
          <span>{typeLabel}</span>
        </div>
        <div className="action-card-meta">
          {modelLabel && (
            <span className="action-card-model">{modelLabel}</span>
          )}
          {confidence_score != null && (
            <span className="action-card-confidence">{Math.round(confidence_score)}%</span>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="action-card-body">
        <p className="action-card-description">{action_description || 'Solomon wants to perform an action.'}</p>
        {mcp.argsText && (
          <pre className="action-card-args" aria-label="Tool arguments">{mcp.argsText}</pre>
        )}
        {!reversible && (
          <p className="action-card-irreversible-warning">
            This action cannot be undone once executed.
          </p>
        )}
        {mcp.isPaid && (
          <p className="action-card-paid-note">Paid tool — this call may cost money.</p>
        )}
      </div>

      {cardError && <p className="action-card-error" role="status">{cardError}</p>}

      {/* Action buttons */}
      <div className="action-card-buttons">
        <button
          className="action-card-btn action-card-btn--approve"
          onClick={handleApprove}
          disabled={!!loading}
        >
          {loading === 'approve' ? 'Approving...' : 'Approve'}
          {!loading && <CheckIcon />}
        </button>

        {mcp.showAlwaysAllow && (
          <button
            className="action-card-btn action-card-btn--always-allow"
            onClick={async () => {
              setLoading('always-allow'); setCardError(null);
              try { await onAlwaysAllow?.(id, mcp.serverId, mcp.toolName); }
              catch (err) { setCardError('Could not allow that tool — try approving it instead.'); console.warn('[Solomon] always-allow failed:', err); }
              finally { setLoading(null); }
            }}
            disabled={!!loading}
            title="Runs this now, and auto-runs future calls to this tool"
          >
            {loading === 'always-allow' ? 'Allowing...' : 'Always allow this tool'}
          </button>
        )}

        {mcp.showAllowForChat && (
          <button
            className="action-card-btn action-card-btn--always-allow"
            onClick={async () => {
              setLoading('allow-for-chat'); setCardError(null);
              try { await onAllowForChat?.(id, mcp.serverId, mcp.toolName, mcp.conversationId); }
              catch (err) { setCardError('Could not allow that tool for this chat — try approving it instead.'); console.warn('[Solomon] allow-for-chat failed:', err); }
              finally { setLoading(null); }
            }}
            disabled={!!loading}
            title="Runs this now, and auto-runs it for the rest of this conversation only"
          >
            {loading === 'allow-for-chat' ? 'Allowing...' : 'Allow for this chat'}
          </button>
        )}

        {onEdit && (
          <button
            className="action-card-btn action-card-btn--edit"
            onClick={() => onEdit?.(id, action)}
            disabled={!!loading}
          >
            Edit
            <EditIcon />
          </button>
        )}

        <button
          className="action-card-btn action-card-btn--cancel"
          onClick={handleCancel}
          disabled={!!loading}
        >
          {loading === 'cancel' ? 'Cancelling...' : 'Cancel'}
          {!loading && <XIcon />}
        </button>
      </div>

      {mcp.isMcp && (
        <button className="action-card-link" onClick={() => openSettings('mcp')}>
          Manage connections
        </button>
      )}
    </div>
  );
}

// ── Icons ────────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
